import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { parentTwoFactorLogin, getOnboardingStatus } from '../../services/api';

// ─── Brand tokens (parent interface) ──────────────────────────────────────────

const C = {
  bg: '#FFFFFF',
  surface: '#FFFFFF',
  surfaceMuted: '#F8FAFC',
  text: '#1E293B',
  textMuted: '#64748B',
  textSubtle: '#94A3B8',
  accent: '#0066FF',
  accentDark: '#0052CC',
  accentDarker: '#003D99',
  accentSoft: '#EFF4FF',
  accentBorder: '#C7D7FE',
  border: '#E2E8F0',
  borderStrong: '#CBD5E1',
  red: '#EF4444',
  redSoft: '#FEF2F2',
  redBorder: '#FECACA',
  redText: '#B91C1C',
};

const HEADING_FONT = "'Syne', sans-serif";
const BODY_FONT = "'DM Sans', sans-serif";

const CODE_LENGTH = 6;

// ─── Brand mark ───────────────────────────────────────────────────────────────

function LanternBrand() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center' }}>
      <div style={{
        width: 32, height: 32, borderRadius: 9,
        background: `linear-gradient(135deg, ${C.accent} 0%, ${C.accentDarker} 100%)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: `0 2px 8px ${C.accent}55`,
      }}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <rect x="5" y="3" width="6" height="8" rx="1" stroke="#fff" strokeWidth="1.4"/>
          <path d="M6.5 3V2h3v1M5 11h6M7 13h2" stroke="#fff" strokeWidth="1.4" strokeLinecap="round"/>
        </svg>
      </div>
      <span style={{
        fontFamily: HEADING_FONT, fontSize: 18, fontWeight: 700,
        color: C.text, letterSpacing: '-0.01em',
      }}>Lantern</span>
    </div>
  );
}

// ─── Main 2FA screen ──────────────────────────────────────────────────────────

function friendlyError(err) {
  const status = err?.response?.status;
  const serverMessage = err?.response?.data?.error;
  if (status === 401 && /expired/i.test(serverMessage || '')) {
    return { message: 'This sign-in attempt has expired. Please log in again.', expired: true };
  }
  if (status === 401) {
    return { message: 'That code didn’t work — try again.', expired: false };
  }
  if (status === 400) {
    return { message: serverMessage || 'Please enter the 6-digit code from your authenticator app.', expired: false };
  }
  return { message: 'Something went wrong. Please try again in a moment.', expired: false };
}

export default function ParentTwoFactor() {
  const navigate = useNavigate();
  const location = useLocation();
  const tempToken = location.state?.tempToken;

  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // No tempToken → bounce back to login. ParentLogin should be the only caller.
  useEffect(() => {
    if (!tempToken) {
      navigate('/parent/login', { replace: true });
    }
  }, [tempToken, navigate]);

  const submit = React.useCallback(async (fullCode) => {
    if (submitting || !tempToken) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await parentTwoFactorLogin(tempToken, fullCode);
      const accessToken = res?.data?.accessToken;
      if (!accessToken) {
        setError('Something went wrong. Please log in again.');
        setCode('');
        setSubmitting(false);
        return;
      }
      localStorage.setItem('accessToken', accessToken);
      // Route new parents through the onboarding wizard. Falls back to the
      // dashboard if the status check fails — better than getting stuck.
      try {
        const statusRes = await getOnboardingStatus();
        navigate(statusRes.data?.onboardingComplete ? '/parent/dashboard' : '/parent/onboarding');
      } catch {
        navigate('/parent/dashboard');
      }
    } catch (err) {
      const { message, expired } = friendlyError(err);
      if (expired) {
        // Server has invalidated this temp token — bounce to login with the message.
        navigate('/parent/login', { replace: true, state: { error: message } });
        return;
      }
      setError(message);
      setCode('');
      setSubmitting(false);
    }
  }, [submitting, tempToken, navigate]);

  // Auto-submit the moment the code reaches 6 digits — standard TOTP UX.
  useEffect(() => {
    if (code.length === CODE_LENGTH && !submitting) {
      submit(code);
    }
  }, [code, submitting, submit]);

  function handleDigit(d) {
    setError('');
    setCode(c => (c.length >= CODE_LENGTH ? c : c + d));
  }

  function handleDelete() {
    setError('');
    setCode(c => c.slice(0, -1));
  }

  // Desktop keyboard support — accept digits and Backspace directly so a
  // parent on a laptop can type their TOTP code without clicking the pad.
  useEffect(() => {
    function onKeyDown(e) {
      if (submitting) return;
      if (/^[0-9]$/.test(e.key)) {
        e.preventDefault();
        setError('');
        setCode(c => (c.length >= CODE_LENGTH ? c : c + e.key));
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        setError('');
        setCode(c => c.slice(0, -1));
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [submitting]);

  // Render nothing while the redirect-effect fires (no tempToken case).
  if (!tempToken) return null;

  const numberPad = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    [null, '0', 'del'],
  ];

  return (
    <>
      <style>{`
        @keyframes p2fa-shake {
          0%, 100% { transform: translateX(0); }
          20%, 60% { transform: translateX(-5px); }
          40%, 80% { transform: translateX(5px); }
        }
        .p2fa-padbtn {
          height: 64px;
          width: 64px;
          border-radius: 50%;
          border: 1.5px solid ${C.border};
          background: ${C.surface};
          color: ${C.text};
          font-family: ${HEADING_FONT};
          font-size: 24px;
          font-weight: 700;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 160ms ease, border-color 160ms ease, transform 90ms ease, box-shadow 160ms ease;
          box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
          user-select: none;
          -webkit-tap-highlight-color: transparent;
        }
        .p2fa-padbtn:hover:not(:disabled) {
          background: ${C.accentSoft};
          border-color: ${C.accentBorder};
          color: ${C.accentDark};
        }
        .p2fa-padbtn:active:not(:disabled) {
          transform: translateY(1px) scale(0.97);
          background: ${C.accentSoft};
        }
        .p2fa-padbtn:focus-visible {
          outline: none;
          border-color: ${C.accent};
          box-shadow: 0 0 0 4px ${C.accent}22;
        }
        .p2fa-padbtn--del {
          font-size: 18px;
          color: ${C.textMuted};
        }
        .p2fa-padbtn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .p2fa-shake { animation: p2fa-shake 360ms ease; }
        @keyframes p2fa-spin { to { transform: rotate(360deg); } }
      `}</style>

      <div style={{
        minHeight: '100vh',
        background: `${C.bg} radial-gradient(ellipse 600px 400px at 50% -10%, ${C.accent}1c 0%, ${C.accent}00 60%)`,
        fontFamily: BODY_FONT,
        color: C.text,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '40px 20px 60px',
      }}>
        <div style={{ width: '100%', maxWidth: 420 }}>
          <div style={{ marginBottom: 24 }}>
            <LanternBrand />
          </div>

          <div style={{
            background: C.surface,
            border: `1px solid ${C.border}`,
            borderRadius: 16,
            padding: '32px 28px',
            boxShadow: '0 1px 3px rgba(15,23,42,0.04), 0 8px 24px rgba(15,23,42,0.06)',
          }}>
            {/* Shield icon */}
            <div style={{
              width: 48, height: 48, borderRadius: 12,
              background: C.accentSoft,
              border: `1px solid ${C.accentBorder}`,
              margin: '0 auto 14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: C.accent,
            }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M12 2.5l8 3v5.5c0 4.5-3.3 8.3-8 9.5-4.7-1.2-8-5-8-9.5V5.5l8-3z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
                <path d="M9 12l2.2 2.2L15 10.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>

            <h1 style={{
              margin: 0,
              fontFamily: HEADING_FONT,
              fontSize: 24,
              fontWeight: 700,
              color: C.text,
              letterSpacing: '-0.02em',
              textAlign: 'center',
            }}>Two-step verification</h1>
            <p style={{
              margin: '6px auto 24px',
              fontSize: 14,
              color: C.textMuted,
              textAlign: 'center',
              maxWidth: 340,
              lineHeight: 1.5,
            }}>
              Enter the 6-digit code from your authenticator app.
            </p>

            {/* Code dots */}
            <div
              className={error ? 'p2fa-shake' : undefined}
              aria-live="polite"
              aria-label={`Code: ${code.length} of ${CODE_LENGTH} digits entered`}
              style={{
                display: 'flex',
                gap: 10,
                justifyContent: 'center',
                alignItems: 'center',
                padding: '14px 16px',
                background: error ? C.redSoft : C.surfaceMuted,
                border: `1.5px solid ${error ? C.redBorder : C.border}`,
                borderRadius: 12,
                marginBottom: 20,
                minHeight: 56,
                transition: 'background 160ms ease, border-color 160ms ease',
              }}
            >
              {Array.from({ length: CODE_LENGTH }).map((_, i) => {
                const filled = i < code.length;
                return (
                  <span
                    key={i}
                    aria-hidden="true"
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: '50%',
                      background: filled ? C.accent : 'transparent',
                      border: `2px solid ${filled ? C.accent : C.borderStrong}`,
                      boxShadow: filled ? `0 1px 3px ${C.accent}55` : 'none',
                      transition: 'background 120ms ease, border-color 120ms ease',
                    }}
                  />
                );
              })}
            </div>

            {/* Number pad */}
            <div
              role="group"
              aria-label="6-digit code input"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 12,
                justifyItems: 'center',
              }}
            >
              {numberPad.flat().map((key, idx) => {
                if (key === null) {
                  return <div key={`blank-${idx}`} aria-hidden="true" style={{ width: 64, height: 64 }} />;
                }
                if (key === 'del') {
                  return (
                    <button
                      key="del"
                      type="button"
                      className="p2fa-padbtn p2fa-padbtn--del"
                      onClick={handleDelete}
                      disabled={submitting || code.length === 0}
                      aria-label="Delete last digit"
                    >
                      <svg width="20" height="16" viewBox="0 0 22 18" fill="none">
                        <path d="M7 1 L21 1 Q21 1 21 3 L21 15 Q21 17 19 17 L7 17 L1 9 Z"
                          stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" fill="none" />
                        <path d="M11 6 L16 11 M16 6 L11 11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                      </svg>
                    </button>
                  );
                }
                return (
                  <button
                    key={key}
                    type="button"
                    className="p2fa-padbtn"
                    onClick={() => handleDigit(key)}
                    disabled={submitting || code.length >= CODE_LENGTH}
                    aria-label={`Digit ${key}`}
                  >
                    {key}
                  </button>
                );
              })}
            </div>

            {/* Status / error / submitting */}
            {submitting && (
              <div style={{
                marginTop: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                color: C.textMuted,
                fontSize: 13,
              }}>
                <span style={{
                  width: 14, height: 14, borderRadius: '50%',
                  border: `2px solid ${C.border}`,
                  borderTopColor: C.accent,
                  animation: 'p2fa-spin 700ms linear infinite',
                }}/>
                Verifying&hellip;
              </div>
            )}

            {error && !submitting && (
              <div role="alert" style={{
                marginTop: 16,
                padding: '10px 14px',
                background: C.redSoft,
                border: `1px solid ${C.redBorder}`,
                borderRadius: 10,
                color: C.redText,
                fontSize: 13,
                fontWeight: 500,
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <circle cx="8" cy="8" r="7" stroke={C.redText} strokeWidth="1.5"/>
                  <path d="M8 4v5M8 11.5v.5" stroke={C.redText} strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                {error}
              </div>
            )}
          </div>

          <p style={{
            marginTop: 18,
            textAlign: 'center',
            fontSize: 14,
            color: C.textMuted,
          }}>
            <Link
              to="/parent/login"
              style={{ color: C.accent, fontWeight: 600, textDecoration: 'none' }}
            >
              Back to sign in
            </Link>
          </p>
        </div>
      </div>
    </>
  );
}
